import { supabase } from '../supabaseClient'

// Utility function to fetch all records with pagination to remove 1000 record limit
const fetchAllRecords = async (queryOrFn, batchSize = 1000) => {
  let allData = []
  let start = 0
  let hasMore = true

  while (hasMore) {
    const q = typeof queryOrFn === 'function' ? queryOrFn() : queryOrFn
    const { data, error } = await q.range(start, start + batchSize - 1)
    
    if (error) {
      throw error
    }
    
    if (data && data.length > 0) {
      allData = allData.concat(data)
      
      // If we got less than batchSize, we've reached the end
      if (data.length < batchSize) {
        hasMore = false
      } else {
        start += batchSize
      }
    } else {
      hasMore = false
    }
  }
  
  return allData
}

export const billingService = {
  // Get all bills with pagination and sorting
  async getBills(page = 1, limit = 50, orderBy = 'billedmonth', ascending = false) {
    try {
      const start = (page - 1) * limit
      const end = start + limit - 1

      // Fetch bills
      const { data: bills, error, count } = await supabase
        .from('bills')
        .select('*', { count: 'exact' })
        .order(orderBy, { ascending })
        .range(start, end)

      if (error) throw error

      // Get unique customer IDs
      const customerIds = [...new Set(bills.map(b => b.customerid).filter(id => id))]

      // Fetch customers
      const { data: customers } = await supabase
        .from('customers')
        .select('customerid, name, address, type, barangay')
        .in('customerid', customerIds)
        .limit(10000)

      // Create customer lookup map
      const customerMap = {}
      if (customers) {
        customers.forEach(c => {
          customerMap[c.customerid] = c
        })
      }

      // Manually join customer data to bills
      const billsWithCustomers = bills.map(bill => ({
        ...bill,
        customers: customerMap[bill.customerid] || null
      }))

      return { success: true, data: billsWithCustomers, count }
    } catch (error) {
      console.error('Error getting bills:', error)
      return { success: false, error: error.message, data: [], count: 0 }
    }
  },

  // Get customer types with rates
  async getCustomerTypes() {
    try {
      const baseQuery = supabase
        .from('customer_type')
        .select('type, rate1, rate2')
        .order('type', { ascending: true })

      const data = await fetchAllRecords(baseQuery)
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error getting customer types:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  // Get customer's last bill
  async getLastBill(customerId) {
    try {
      const { data, error } = await supabase
        .from('bills')
        .select('*')
        .eq('customerid', customerId)
        .order('billedmonth', { ascending: false })
        .limit(1)
        .single()

      if (error && error.code !== 'PGRST116') throw error
      return { success: true, data: data || null }
    } catch (error) {
      console.error('Error getting last bill:', error)
      return { success: false, error: error.message, data: null }
    }
  },

  // Check if bill already exists for customer and month
  async checkDuplicateBill(customerId, billedMonth, excludeBillId = null) {
    try {
      let query = supabase
        .from('bills')
        .select('billid')
        .eq('customerid', customerId)
        .eq('billedmonth', billedMonth)

      if (excludeBillId) {
        query = query.neq('billid', excludeBillId)
      }

      const { data, error } = await query

      if (error) throw error
      return { success: true, exists: data && data.length > 0 }
    } catch (error) {
      console.error('Error checking duplicate bill:', error)
      return { success: false, error: error.message, exists: false }
    }
  },

  // Check if a bill ID already exists
  async checkBillIdExists(billId) {
    try {
      const { data, error } = await supabase
        .from('bills')
        .select('billid')
        .eq('billid', billId)
        .maybeSingle()

      if (error) throw error
      return { success: true, data: { exists: data !== null } }
    } catch (error) {
      console.error('Error checking bill ID exists:', error)
      return { success: false, error: error.message, data: { exists: false } }
    }
  },

  // Calculate basic amount based on consumption and customer type
  calculateBasicAmount(consumption, customerType) {
    if (!customerType) return 0

    const rate1 = parseFloat(customerType.rate1 || 0)
    const rate2 = parseFloat(customerType.rate2 || 0)
    let basic = 0

    if (consumption === 0) {
      // Minimum charge for 0 consumption
      basic = rate1
    } else if (consumption <= 3) {
      // First 3 cubic meters charged at rate1
      basic = consumption * rate1
    } else {
      // First 3 at rate1, excess at rate2
      basic = (3 * rate1) + ((consumption - 3) * rate2)
    }

    return parseFloat(basic.toFixed(2))
  },

  // Calculate discount amount based on customer discount percentage
  calculateDiscountAmount(basicAmount, discountPercent, consumption = null) {
    if (consumption !== null && consumption <= 1) return 0
    if (!discountPercent || discountPercent <= 0) return 0
    return (basicAmount * discountPercent) / 100
  },

  // Add new bill
  async addBill(billData) {
    try {
      const { data, error } = await supabase
        .from('bills')
        .insert([billData])
        .select()
        .single()

      if (error) throw error
      return { success: true, data }
    } catch (error) {
      console.error('Error adding bill:', error)
      return { success: false, error: error.message }
    }
  },

  // Update existing bill
  async updateBill(billId, billData) {
    try {
      const { data, error } = await supabase
        .from('bills')
        .update(billData)
        .eq('billid', billId)
        .select()
        .single()

      if (error) throw error
      return { success: true, data }
    } catch (error) {
      console.error('Error updating bill:', error)
      return { success: false, error: error.message }
    }
  },

  // Delete bill
  async deleteBill(billId) {
    try {
      const { error } = await supabase
        .from('bills')
        .delete()
        .eq('billid', billId)

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error deleting bill:', error)
      return { success: false, error: error.message }
    }
  },

  // Get bills by customer
  async getCustomerBills(customerId) {
    try {
      const baseQuery = supabase
        .from('bills')
        .select('*')
        .eq('customerid', customerId)
        .order('billedmonth', { ascending: false })

      const data = await fetchAllRecords(baseQuery)
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error getting customer bills:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  // Get bills by month with customer details for reading sheets
  async getBillsByMonth(billedMonth) {
    try {
      // Convert YYYY-MM format to YYYY-MM-01 for database query
      const formattedMonth = billedMonth + '-01'
      
      // Fetch all bills for the specified month with batch processing
      let allBills = []
      let start = 0
      const batchSize = 1000
      let hasMore = true

      while (hasMore) {
        const { data: billBatch, error: billError } = await supabase
          .from('bills')
          .select('*')
          .eq('billedmonth', formattedMonth)
          .order('customerid', { ascending: true })
          .range(start, start + batchSize - 1)

        if (billError) throw billError

        if (billBatch && billBatch.length > 0) {
          allBills = [...allBills, ...billBatch]
          if (billBatch.length < batchSize) {
            hasMore = false
          } else {
            start += batchSize
          }
        } else {
          hasMore = false
        }
      }

      if (allBills.length === 0) {
        return { success: true, data: [] }
      }

      // Get unique customer IDs
      const customerIds = [...new Set(allBills.map(b => b.customerid).filter(id => id))]

      // Fetch customer details in batches
      let allCustomers = []
      start = 0
      hasMore = true
      
      // Reduce batch size for customer fetch to avoid URL length limits
      // The error suggests the URL is too long due to many IDs in the 'in' filter
      const customerBatchSize = 50 

      while (hasMore) {
        const batchIds = customerIds.slice(start, start + customerBatchSize)
        if (batchIds.length === 0) break

        const { data: customerBatch, error: customerError } = await supabase
          .from('customers')
          .select('customerid, name, type, barangay, status, address')
          .in('customerid', batchIds)

        if (customerError) throw customerError

        if (customerBatch && customerBatch.length > 0) {
          allCustomers = [...allCustomers, ...customerBatch]
        }

        start += customerBatchSize
        if (batchIds.length < customerBatchSize) {
          hasMore = false
        }
      }

      // Create customer lookup map
      const customerMap = {}
      allCustomers.forEach(c => {
        customerMap[c.customerid] = c
      })

      // Merge bill and customer data
      const billsWithCustomers = allBills.map(bill => {
        const customer = customerMap[bill.customerid]
        return {
          billid: bill.billid,
          customerid: bill.customerid,
          name: customer?.name || 'Unknown',
          type: customer?.type || 'Unknown',
          barangay: customer?.barangay || 'Unknown',
          status: customer?.status || 'Unknown',
          address: customer?.address || '',
          meterno: bill.meterno || customer?.meterno || '',
          sequence: bill.sequence || customer?.sequence || '',
          billedmonth: bill.billedmonth,
          previousreading: bill.previousreading,
          currentreading: bill.currentreading,
          consumption: bill.consumption
        }
      })

      return { success: true, data: billsWithCustomers }
    } catch (error) {
      console.error('Error getting bills by month:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  // Get encoding statistics by barangay for a specific month
  async getEncodingStats(billedMonth) {
    try {
      // Fetch all customers in batches to overcome Supabase's 1000-row default limit
      let allCustomers = []
      let start = 0
      const batchSize = 1000
      let hasMore = true

      while (hasMore) {
        const { data: customerBatch, error: customerError } = await supabase
          .from('customers')
          .select('customerid, name, barangay, type, status, address, meterno')
          .order('barangay', { ascending: true })
          .range(start, start + batchSize - 1)

        if (customerError) throw customerError

        if (customerBatch && customerBatch.length > 0) {
          allCustomers = [...allCustomers, ...customerBatch]
          if (customerBatch.length < batchSize) {
            hasMore = false
          } else {
            start += batchSize
          }
        } else {
          hasMore = false
        }
      }

      // Fetch all bills for the specified month in batches
      let allBills = []
      start = 0
      hasMore = true

      while (hasMore) {
        const { data: billBatch, error: billError } = await supabase
          .from('bills')
          .select('customerid')
          .eq('billedmonth', billedMonth)
          .range(start, start + batchSize - 1)

        if (billError) throw billError

        if (billBatch && billBatch.length > 0) {
          allBills = [...allBills, ...billBatch]
          if (billBatch.length < batchSize) {
            hasMore = false
          } else {
            start += batchSize
          }
        } else {
          hasMore = false
        }
      }

      const billedCustomerIds = new Set(allBills.map(b => b.customerid))

      console.log(`Encoding Stats: Fetched ${allCustomers.length} total customers and ${allBills.length} bills for ${billedMonth}`)

      // Group customers by barangay with ALL customer data
      const barangayStats = {}
      allCustomers.forEach(customer => {
        const barangay = customer.barangay || 'Unknown'
        if (!barangayStats[barangay]) {
          barangayStats[barangay] = {
            barangay,
            totalCustomers: 0,
            encodedCount: 0,
            allCustomers: [], // Store all customers for filtering
            notEncodedCustomers: []
          }
        }
        
        const customerData = {
          customerid: customer.customerid,
          name: customer.name,
          type: customer.type,
          status: customer.status,
          address: customer.address,
          meterno: customer.meterno,
          isEncoded: billedCustomerIds.has(customer.customerid)
        }
        
        barangayStats[barangay].allCustomers.push(customerData)
        barangayStats[barangay].totalCustomers++
        
        if (billedCustomerIds.has(customer.customerid)) {
          barangayStats[barangay].encodedCount++
        } else {
          barangayStats[barangay].notEncodedCustomers.push({
            customerid: customer.customerid,
            name: customer.name,
            type: customer.type,
            status: customer.status,
            address: customer.address,
            meterno: customer.meterno
          })
        }
      })

      return { 
        success: true, 
        data: Object.values(barangayStats).sort((a, b) => a.barangay.localeCompare(b.barangay))
      }
    } catch (error) {
      console.error('Error getting encoding stats:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  // Get customers with unpaid bills for notice generation
  async getCustomersWithUnpaidBills(options = {}) {
    try {
      // Fetch surcharge settings to get due_day
      const { data: surchargeSettings, error: surchargeError } = await supabase
        .from('surcharge_settings')
        .select('*')
        .single()

      if (surchargeError) {
        console.error('Error fetching surcharge settings:', surchargeError)
        // Default to day 20 if settings not found
      }

      const dueDay = surchargeSettings?.due_day || 20
      const today = new Date()
      today.setHours(0, 0, 0, 0) // Set to start of day for comparison

      // Fetch all unpaid bills using pagination utility
      let baseQueryBills = supabase
        .from('bills')
        .select('billid, customerid, billedmonth, basicamount, surchargeamount, totalbillamount')
        .eq('paymentstatus', 'Unpaid')
        .order('customerid', { ascending: true })
        .order('billedmonth', { ascending: true })

      // If monthFilter is provided, we still need to fetch ALL unpaid bills for the customers
      // who match the filter, OR just the filtered bills?
      // "Generate notice per month" usually implies generating notices for a specific billing cycle.
      // However, notices should typically reflect TOTAL outstanding balance.
      // If options.monthFilter is set, we will first find customers who have an unpaid bill for that month.
      
      const bills = await fetchAllRecords(baseQueryBills)

      if (!bills || bills.length === 0) {
        return { success: true, data: [], totalCustomers: 0 }
      }

      // Filter out bills that are not yet due
      const overdueBills = bills.filter(bill => {
        // If specific month requested, check if this bill matches
        // But we want to return customers who match the filter, with ALL their overdue bills?
        // Or just the bills for that month?
        // Let's assume: Notice is for TOTAL overdue, but triggered by specific month being overdue.
        
        const billedMonth = new Date(bill.billedmonth)
        // Due date is the due_day of the month after billing month
        const dueDate = new Date(billedMonth.getFullYear(), billedMonth.getMonth() + 1, dueDay)
        dueDate.setHours(0, 0, 0, 0)
        
        // Only include bills where due date has passed
        return today > dueDate
      })

      if (overdueBills.length === 0) {
        return { success: true, data: [], totalCustomers: 0 }
      }

      // If month filter is applied, identify valid customer IDs first
      let validCustomerIds = null;
      if (options.monthFilter) {
        // format: YYYY-MM-DD or YYYY-MM
        const filterMonthStr = options.monthFilter.substring(0, 7); // Ensure YYYY-MM
        
        const matchingBills = overdueBills.filter(b => b.billedmonth.startsWith(filterMonthStr));
        validCustomerIds = new Set(matchingBills.map(b => b.customerid));
        
        if (validCustomerIds.size === 0) {
             return { success: true, data: [], totalCustomers: 0 }
        }
      }

      // Get unique customer IDs from overdue bills only
      // If filtering, intersect with validCustomerIds
      let customerIds = [...new Set(overdueBills.map(b => b.customerid).filter(id => id))]
      
      if (validCustomerIds) {
          customerIds = customerIds.filter(id => validCustomerIds.has(id));
      }

      // Fetch customers separately using pagination utility
      const baseQueryCustomers = supabase
        .from('customers')
        .select('customerid, name, barangay, type, status, address, meterno')
        .in('customerid', customerIds)

      const customers = await fetchAllRecords(baseQueryCustomers)

      // Create customer lookup map
      const customerMap = {}
      if (customers) {
        customers.forEach(c => {
          customerMap[c.customerid] = c
        })
      }

      // Group overdue bills by customer with customer details
      const customerBillsMap = {}
      
      overdueBills.forEach(bill => {
        // If filtering, only process bills for valid customers
        if (validCustomerIds && !validCustomerIds.has(bill.customerid)) {
            return;
        }

        // If month filter is active, only include bills matching the month
        if (options.monthFilter) {
          const filterMonthStr = options.monthFilter.substring(0, 7);
          if (!bill.billedmonth.startsWith(filterMonthStr)) {
            return;
          }
        }

        const customerId = bill.customerid
        const customerInfo = customerMap[customerId]
        
        if (!customerBillsMap[customerId]) {
          customerBillsMap[customerId] = {
            customerid: customerId,
            name: customerInfo?.name || 'Unknown',
            barangay: customerInfo?.barangay || '',
            type: customerInfo?.type || '',
            status: customerInfo?.status || '',
            address: customerInfo?.address || '',
            meterno: customerInfo?.meterno || '',
            unpaidBills: [],
            totalBasicAmount: 0,
            totalSurcharge: 0,
            totalBilledAmount: 0,
            billCount: 0,
            earliestMonth: bill.billedmonth,
            latestMonth: bill.billedmonth
          }
        }
        
        const customer = customerBillsMap[customerId]
        customer.unpaidBills.push(bill)
        customer.totalBasicAmount += parseFloat(bill.basicamount || 0)
        customer.totalSurcharge += parseFloat(bill.surchargeamount || 0)
        customer.totalBilledAmount += parseFloat(bill.totalbillamount || 0)
        customer.billCount++
        
        // Update month range
        if (bill.billedmonth < customer.earliestMonth) {
          customer.earliestMonth = bill.billedmonth
        }
        if (bill.billedmonth > customer.latestMonth) {
          customer.latestMonth = bill.billedmonth
        }
      })

      // Convert to array and add bill scope
      const customersWithUnpaidBills = Object.values(customerBillsMap).map(customer => ({
        ...customer,
        billScope: customer.earliestMonth === customer.latestMonth 
          ? customer.earliestMonth 
          : `${customer.earliestMonth} to ${customer.latestMonth}`
      }))

      return { 
        success: true, 
        data: customersWithUnpaidBills,
        totalCustomers: customersWithUnpaidBills.length
      }
    } catch (error) {
      console.error('Error getting customers with unpaid bills:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  // Get recent bills
  async getRecentBills(limit = 5) {
    try {
      const { data: bills, error } = await supabase
        .from('bills')
        .select('*')
        .order('dateencoded', { ascending: false })
        .limit(limit)

      if (error) throw error

      // Get unique customer IDs
      const customerIds = [...new Set(bills.map(b => b.customerid).filter(id => id))]

      // Fetch customers
      const { data: customers } = await supabase
        .from('customers')
        .select('customerid, name, address, type, barangay')
        .in('customerid', customerIds)

      // Create customer lookup map
      const customerMap = {}
      if (customers) {
        customers.forEach(c => {
          customerMap[c.customerid] = c
        })
      }

      // Join customer data to bills
      const billsWithCustomers = bills.map(bill => ({
        ...bill,
        customers: customerMap[bill.customerid] || null
      }))

      return { success: true, data: billsWithCustomers }
    } catch (error) {
      console.error('Error getting recent bills:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  // Export bills to CSV
  exportToCSV(bills) {
    const headers = [
      'Bill ID', 'Customer ID', 'Customer Name', 'Barangay', 
      'Billed Month', 'Previous Reading', 'Current Reading', 'Consumption',
      'Basic Amount', 'Surcharge', 'Discount', 'Total Bill', 'Payment Status'
    ]

    const rows = bills.map(bill => [
      bill.billid,
      bill.customerid,
      bill.customers?.name || '',
      bill.customers?.barangay || '',
      bill.billedmonth,
      bill.previousreading || 0,
      bill.currentreading || 0,
      bill.consumption || 0,
      bill.basicamount || 0,
      bill.surchargeamount || 0,
      bill.discountamount || 0,
      bill.totalbillamount || 0,
      bill.paymentstatus || ''
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n')

    return csvContent
  }
}
